"use client";
import OtrasOpciones from "../components/OtrasOpciones";
import LayoutTransporte from "../components/layoutTransporte";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { FaWhatsapp } from "react-icons/fa";
import { VscCallOutgoing } from "react-icons/vsc";
import { CiMail } from "react-icons/ci";
import {
  ChevronDown,
  MapPin,
  Plane,
  Calendar,
  Clock,
  Users,
  Search,
  X,
  Car,
  UserCircle2,
  ShieldCheck,
  Luggage,
  BadgeCheck,
  ArrowDown,
} from "lucide-react";
import Image from "next/image";
import MapButton from "../../../components/MapButton";
import TaxiReservaModal from "../../../components/TaxiReservaModal";
import TarifasTaxi from "./TarifasTaxi";
import {
  calcularPrecio,
  formatearPrecio,
  TARIFA_TIPOS,
  type ReservaTaxiPendiente,
  type Tarifa,
  type TipoVehiculo,
} from "../../../lib/api/taxi/types";
import {
  refrescarTarifas,
  useTarifas,
} from "../../../lib/api/taxi/tarifasStore";
import { ToastContainer } from "../../../components/Toast";
import { useToast } from "../../../hooks/useToast";
import { fetchVuelos } from "../../../lib/api/vuelos/fetchVuelos";
import type { Vuelos } from "../../../lib/api/vuelos/types";
import { pickLocalized } from "../../../components/RegionalLayout/types";
import { toEsEn, type Locale } from "@/i18n/config";
import { useLocale } from "next-intl";

/**
 * Reserva en línea apagada temporalmente. Mientras esté en `false` la página
 * esconde el CTA "Reservar ahora", el formulario de cálculo y las tarjetas de
 * vehículos, y en su lugar enseña el listado de precios (`TarifasTaxi`). El
 * código de la reserva se queda entero a propósito: para reactivarla basta con
 * poner `true`.
 */
const RESERVAS_ACTIVAS = false;

/** Único aeropuerto con reservas de taxi en línea por ahora. */
const AEROPUERTO_IATA = "PTY";

/** YYYY-MM-DD en hora de Panamá (UTC-5, sin horario de verano), igual que
 *  `fetchVuelos`. Con `toISOString()` a secas la fecha se adelanta un día
 *  después de las 19:00 hora local. */
function fechaPanama(offsetDias = 0): string {
  const panama = new Date(Date.now() - 5 * 60 * 60 * 1000);
  panama.setUTCDate(panama.getUTCDate() + offsetDias);
  return panama.toISOString().slice(0, 10);
}

/** El `datetime` del API de vuelos viene como "2026-08-20 19:31:00", hora local
 *  de Panamá y sin zona. Lo partimos con regex en vez de pasarlo por `Date`
 *  para que un navegador en otra zona horaria no corra la hora. */
function partirDatetimeVuelo(
  datetime: string,
): { fecha: string; hora: string } | null {
  const m = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/.exec(datetime.trim());
  return m ? { fecha: m[1], hora: m[2] } : null;
}

export default function TaxiPage() {
  const t = useTranslations("taxiPage");
  const locale = useLocale() as Locale;
  const { toasts, showToast, closeToast } = useToast();

  // Catálogo de tarifas: se descarga de fondo en cuanto el navegador queda
  // libre, así que para cuando el usuario baja al formulario ya está en memoria.
  const {
    tarifas,
    cargando: tarifasCargando,
    error: tarifasError,
  } = useTarifas(RESERVAS_ACTIVAS);

  // Estados para el formulario de cálculo
  const [destino, setDestino] = useState<Tarifa | null>(null);
  const [fecha, setFecha] = useState("");
  const [hora, setHora] = useState("");
  const [pasajeros, setPasajeros] = useState(1);
  const [numeroVuelo, setNumeroVuelo] = useState("");
  // Si hay vuelo elegido, fecha y hora salen de él y no se piden aparte.
  const [vueloSeleccionado, setVueloSeleccionado] = useState<Vuelos | null>(
    null,
  );
  const [mostrarResultado, setMostrarResultado] = useState(false);
  const [dropdownPasajerosAbierto, setDropdownPasajerosAbierto] =
    useState(false);
  const [dropdownFechaAbierto, setDropdownFechaAbierto] = useState(false);
  const [dropdownDestinoAbierto, setDropdownDestinoAbierto] = useState(false);
  const [dropdownHoraAbierto, setDropdownHoraAbierto] = useState(false);
  const [dropdownVueloAbierto, setDropdownVueloAbierto] = useState(false);
  const [vuelos, setVuelos] = useState<Vuelos[]>([]);
  const [vuelosCargando, setVuelosCargando] = useState(false);
  const [vuelosCargados, setVuelosCargados] = useState(false);
  const [busquedaVuelo, setBusquedaVuelo] = useState("");
  const [busquedaDestino, setBusquedaDestino] = useState("");

  // Estados para el modal de reserva
  const [modalAbierto, setModalAbierto] = useState(false);
  const [tipoVehiculoSeleccionado, setTipoVehiculoSeleccionado] =
    useState<TipoVehiculo>("sedan");
  const [datosReservaTemp, setDatosReservaTemp] =
    useState<ReservaTaxiPendiente | null>(null);
  // Lo que enseña el modal: el precio de aquí es referencial, el bueno lo
  // devuelve el POST.
  const [resumenReserva, setResumenReserva] = useState<{
    destino: string;
    precio: number;
  } | null>(null);

  const nombreDestino = (tarifa: Tarifa) =>
    pickLocalized(tarifa.destino, locale);

  /** Para buscar sin que estorben tildes ni mayúsculas. */
  const normalizar = (texto: string) =>
    texto
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  // El desplegable agrupa por tipo: un tour de 4 h no se lee igual que un
  // destino suelto o una zona corta de hotel. Los grupos aparecen conforme van
  // llegando del prefetch, sin esperar a que termine todo el catálogo.
  const busquedaDestinoNorm = normalizar(busquedaDestino.trim());
  const gruposDestino = TARIFA_TIPOS.map((tipo) => ({
    tipo,
    tarifas: tarifas.filter(
      (tarifa) =>
        tarifa.tipo === tipo &&
        (!busquedaDestinoNorm ||
          normalizar(nombreDestino(tarifa)).includes(busquedaDestinoNorm)),
    ),
  })).filter((grupo) => grupo.tarifas.length > 0);

  const hayResultadosDestino = gruposDestino.length > 0;
  const precioActual = destino ? calcularPrecio(destino, pasajeros) : 0;

  const cargarVuelos = async () => {
    if (vuelosCargados || vuelosCargando) return;
    setVuelosCargando(true);
    try {
      const [hoy, mañana] = await Promise.all([
        fetchVuelos("arrivals", "hoy", "pty"),
        fetchVuelos("arrivals", "mañana", "pty"),
      ]);
      setVuelos([...hoy, ...mañana]);
      setVuelosCargados(true);
    } catch (e) {
      console.error("Error cargando vuelos:", e);
      showToast(
        "warning",
        "No pudimos cargar los vuelos",
        "Puedes seleccionar TBC o intentar de nuevo más tarde.",
      );
    } finally {
      setVuelosCargando(false);
    }
  };

  const formatearVuelo = (v: Vuelos) => {
    const codigo = `${v.vuelo}${v.vueloNum}`.trim();
    const programado = partirDatetimeVuelo(v.horaEstimada);
    return {
      codigo,
      hora: programado?.hora ?? "",
      fecha: programado?.fecha ?? "",
    };
  };

  /** La lista trae vuelos de hoy y de mañana, y al elegir uno se fija la fecha
   *  de la reserva: sin esta etiqueta dos vuelos iguales serían idénticos. */
  const etiquetaDia = (fechaVuelo: string) => {
    if (!fechaVuelo) return "";
    if (fechaVuelo === fechaPanama()) return t("diaHoy");
    if (fechaVuelo === fechaPanama(1)) return t("diaManana");
    return formatearFecha(fechaVuelo);
  };

  /** Al elegir un vuelo, su fecha y su hora pasan a ser las de la reserva. */
  const elegirVuelo = (v: Vuelos) => {
    const { codigo, fecha: fechaVuelo, hora: horaVuelo } = formatearVuelo(v);
    // Hoy todos los vuelos traen hora programada, pero si alguno llegara sin
    // ella dejamos los selectores a mano en vez de encerrar al pasajero en un
    // resumen vacío que no podría corregir.
    const tieneHorario = Boolean(fechaVuelo && horaVuelo);
    setNumeroVuelo(codigo);
    setVueloSeleccionado(tieneHorario ? v : null);
    setFecha(tieneHorario ? fechaVuelo : "");
    setHora(tieneHorario ? horaVuelo : "");
    setDropdownVueloAbierto(false);
    setBusquedaVuelo("");
  };

  /** TBC: no sabemos el vuelo, así que el usuario elige fecha y hora a mano. */
  const elegirTBC = () => {
    setNumeroVuelo("TBC");
    setVueloSeleccionado(null);
    setFecha("");
    setHora("");
    setDropdownVueloAbierto(false);
    setBusquedaVuelo("");
  };

  const vuelosFiltrados = vuelos
    .filter((v) => {
      if (!busquedaVuelo) return true;
      const q = busquedaVuelo.toLowerCase();
      const { codigo } = formatearVuelo(v);
      return (
        codigo.toLowerCase().includes(q) ||
        (v.routingDescEs || "").toLowerCase().includes(q) ||
        (v.routing || "").toLowerCase().includes(q)
      );
    })
    .slice(0, 80);

  const horas = [
    "06:00",
    "07:00",
    "08:00",
    "09:00",
    "10:00",
    "11:00",
    "12:00",
    "13:00",
    "14:00",
    "15:00",
    "16:00",
    "17:00",
    "18:00",
    "19:00",
    "20:00",
    "21:00",
    "22:00",
  ];

  // Función para obtener la fecha mínima (hoy en Panamá)
  const getFechaMinima = () => fechaPanama();

  // Función para formatear fecha
  const formatearFecha = (fechaString: string) => {
    if (!fechaString) return "";
    const fecha = new Date(fechaString + "T00:00:00");
    return fecha.toLocaleDateString("es-ES", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  };

  // Scroll suave hasta el formulario de reserva
  const scrollToReserva = () => {
    document
      .getElementById("reserva-taxi")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Función para abrir modal de reserva
  const abrirModalReserva = (tipoVehiculo: TipoVehiculo) => {
    if (!destino || !fecha || !hora) {
      const faltantes: string[] = [];
      if (!destino) faltantes.push("destino");
      if (!fecha) faltantes.push("fecha");
      if (!hora) faltantes.push("hora");
      showToast(
        "warning",
        "Faltan datos para continuar",
        `Selecciona ${faltantes.join(", ")} antes de reservar.`,
      );
      return;
    }

    // El precio ya no viaja en la reserva: mandamos la tarifa y el servidor lo
    // calcula. Aquí sólo guardamos el estimado para enseñarlo en el modal.
    setDatosReservaTemp({
      aeropuerto_iata: AEROPUERTO_IATA,
      tarifa_id: destino.id,
      fecha,
      hora,
      cant_pasajero: pasajeros,
      tipo_vehiculo: tipoVehiculo,
      ...(numeroVuelo ? { numero_vuelo: numeroVuelo } : {}),
      lang: toEsEn(locale),
    });
    setResumenReserva({
      destino: nombreDestino(destino),
      precio: calcularPrecio(destino, pasajeros),
    });
    setTipoVehiculoSeleccionado(tipoVehiculo);
    setModalAbierto(true);
  };

  // Cerrar dropdowns al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest(".dropdown-pasajeros")) {
        setDropdownPasajerosAbierto(false);
      }
      if (!target.closest(".dropdown-fecha")) {
        setDropdownFechaAbierto(false);
      }
      if (!target.closest(".dropdown-destino")) {
        setDropdownDestinoAbierto(false);
      }
      if (!target.closest(".dropdown-hora")) {
        setDropdownHoraAbierto(false);
      }
      if (!target.closest(".dropdown-vuelo")) {
        setDropdownVueloAbierto(false);
      }
    };

    if (
      dropdownPasajerosAbierto ||
      dropdownFechaAbierto ||
      dropdownDestinoAbierto ||
      dropdownHoraAbierto ||
      dropdownVueloAbierto
    ) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [
    dropdownPasajerosAbierto,
    dropdownFechaAbierto,
    dropdownDestinoAbierto,
    dropdownHoraAbierto,
    dropdownVueloAbierto,
  ]);

  return (
    <LayoutTransporte
      bannerSrc={
        "https://galeria.tocumenaero.com/gallery/api/Estacionamientos%20T1%20y%20T2/321A3598.jpg"
      }
      cardTitle={t("title")}
    >
      {/* CTA principal: reservar taxi */}
      {RESERVAS_ACTIVAS && (
      <section className="w-full mt-12">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#102542] via-[#1B3B5F] to-[#102542] shadow-xl">
          <div className="absolute -top-16 -right-16 w-72 h-72 bg-[#ffb800]/20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-20 -left-10 w-64 h-64 bg-[#ffb800]/10 rounded-full blur-3xl pointer-events-none" />

          <div className="relative grid grid-cols-1 md:grid-cols-[1fr_auto] items-center gap-8 p-8 md:p-12">
            <div>
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#ffb800]/15 text-[#ffb800] text-xs uppercase tracking-widest font-semibold mb-4">
                <Car className="w-3.5 h-3.5" />
                {t("heroReservaLabel")}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight mb-3">
                {t("heroReservaTitle")}
              </h2>
              <p className="text-white/75 leading-relaxed max-w-xl">
                {t("heroReservaDesc")}
              </p>
            </div>

            <button
              type="button"
              onClick={scrollToReserva}
              className="group inline-flex items-center justify-center gap-2 bg-[#ffb800] hover:bg-white text-[#102542] px-7 py-4 rounded-full font-bold text-base shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-0.5 whitespace-nowrap"
            >
              {t("heroReservaCta")}
              <ArrowDown className="w-5 h-5 transition-transform duration-300 group-hover:translate-y-1" />
            </button>
          </div>
        </div>
      </section>
      )}

      {/* Cualidades del servicio */}
      <section className="w-full mt-24">
        <div className="text-center mb-12">
          <span className="text-[#ffb800] text-sm uppercase tracking-widest font-semibold">
            {t("cualidadesLabel")}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-[#102542] mt-3 mb-3">
            {t("cualidadesTitle")}
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            {t("cualidadesDesc")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="group relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 border border-gray-100">
            <div className="relative h-44 overflow-hidden">
              <Image
                src="https://galeria.tocumenaero.com/gallery/transporte/taxi2.JPG"
                alt={t("cualidad1Title")}
                fill
                sizes="(max-width: 768px) 100vw, 25vw"
                className="object-cover group-hover:scale-110 transition-transform duration-700"
              />
            </div>
            <div className="p-5">
              <div className="w-10 h-10 rounded-lg bg-[#102542] text-[#ffb800] flex items-center justify-center mb-3">
                <Car className="w-5 h-5" />
              </div>
              <h3 className="font-bold text-[#102542] text-lg mb-2">
                {t("cualidad1Title")}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {t("cualidad1Desc")}
              </p>
            </div>
          </div>

          <div className="group relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 border border-gray-100">
            <div className="relative h-44 overflow-hidden">
              <Image
                src="https://galeria.tocumenaero.com/gallery/transporte/847ea07d-a98d-4b60-93e1-3c34b2835685.jpg"
                alt={t("cualidad2Title")}
                fill
                sizes="(max-width: 768px) 100vw, 25vw"
                className="object-cover group-hover:scale-110 transition-transform duration-700"
              />
            </div>
            <div className="p-5">
              <div className="w-10 h-10 rounded-lg bg-[#102542] text-[#ffb800] flex items-center justify-center mb-3">
                <UserCircle2 className="w-5 h-5" />
              </div>
              <h3 className="font-bold text-[#102542] text-lg mb-2">
                {t("cualidad2Title")}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {t("cualidad2Desc")}
              </p>
            </div>
          </div>

          <div className="group relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 border border-gray-100">
            <div className="relative h-44 overflow-hidden">
              <Image
                src="https://galeria.tocumenaero.com/gallery/transporte/taxi1.JPG"
                alt={t("cualidad3Title")}
                fill
                sizes="(max-width: 768px) 100vw, 25vw"
                className="object-cover group-hover:scale-110 transition-transform duration-700"
              />
            </div>
            <div className="p-5">
              <div className="w-10 h-10 rounded-lg bg-[#102542] text-[#ffb800] flex items-center justify-center mb-3">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <h3 className="font-bold text-[#102542] text-lg mb-2">
                {t("cualidad3Title")}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {t("cualidad3Desc")}
              </p>
            </div>
          </div>

          <div className="group relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 border border-gray-100">
            <div className="relative h-44 overflow-hidden">
              <Image
                src="https://galeria.tocumenaero.com/gallery/api/Estacionamientos%20T1%20y%20T2/9M1A9103.jpg"
                alt={t("cualidad4Title")}
                fill
                sizes="(max-width: 768px) 100vw, 25vw"
                className="object-cover group-hover:scale-110 transition-transform duration-700"
              />
            </div>
            <div className="p-5">
              <div className="w-10 h-10 rounded-lg bg-[#102542] text-[#ffb800] flex items-center justify-center mb-3">
                <Luggage className="w-5 h-5" />
              </div>
              <h3 className="font-bold text-[#102542] text-lg mb-2">
                {t("cualidad4Title")}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {t("cualidad4Desc")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Identifica a tu conductor */}
      <section className="w-full mt-24 bg-[#102542] rounded-3xl overflow-hidden relative">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-10 right-10 w-80 h-80 bg-[#ffb800] rounded-full blur-3xl" />
        </div>
        <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-0">
          <div className="p-8 md:p-12 flex flex-col justify-center">
            <span className="text-[#ffb800] text-sm uppercase tracking-widest font-semibold">
              {t("idLabel")}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-3 mb-4">
              {t("idTitle")}
            </h2>
            <p className="text-white/80 leading-relaxed mb-6">{t("idDesc")}</p>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <BadgeCheck className="w-9 h-9 text-[#ffb800] shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-white">
                    {t("idItem1Title")}
                  </p>
                  <p className="text-sm text-white/70 mt-0.5">
                    {t("idItem1Desc")}
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <BadgeCheck className="w-9 h-9 text-[#ffb800] shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-white">
                    {t("idItem2Title")}
                  </p>
                  <p className="text-sm text-white/70 mt-0.5">
                    {t("idItem2Desc")}
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <BadgeCheck className="w-9 h-9 text-[#ffb800] shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-white">
                    {t("idItem3Title")}
                  </p>
                  <p className="text-sm text-white/70 mt-0.5">
                    {t("idItem3Desc")}
                  </p>
                </div>
              </li>
            </ul>
          </div>
          <div className="grid grid-cols-2 gap-3 p-4 lg:p-8">
            <div className="relative h-64 lg:h-auto rounded-2xl overflow-hidden">
              <Image
                src="https://galeria.tocumenaero.com/gallery/transporte/847ea07d-a98d-4b60-93e1-3c34b2835685.jpg"
                alt="Uniforme del conductor"
                fill
                sizes="(max-width: 1024px) 50vw, 25vw"
                className="object-cover"
              />
            </div>
            <div className="flex flex-col gap-3">
              <div className="relative flex-1 min-h-[120px] rounded-2xl overflow-hidden">
                <Image
                  src="https://galeria.tocumenaero.com/gallery/transporte/taxi2.JPG"
                  alt="Conductora mujer con uniforme"
                  fill
                  sizes="(max-width: 1024px) 50vw, 25vw"
                  className="object-cover"
                />
              </div>
              <div className="relative flex-1 min-h-[120px] rounded-2xl overflow-hidden">
                <Image
                  src="https://galeria.tocumenaero.com/gallery/transporte/taxi1.JPG"
                  alt="Vehículo identificado"
                  fill
                  sizes="(max-width: 1024px) 50vw, 25vw"
                  className="object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stands de Taxi */}
      <section className="w-full mt-24">
        <div className="text-center mb-12">
          <span className="text-[#ffb800] text-sm uppercase tracking-widest font-semibold">
            {t("standsLabel")}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-[#102542] mt-3 mb-3">
            {t("standsTitle")}
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">{t("standsDesc")}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="group relative h-80 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-1">
            <Image
              src="https://galeria.tocumenaero.com/gallery/transporte/a02f77e9-a875-49e6-a06a-3aefa98c98a2.jpg"
              alt="Stand de taxis Terminal 1"
              fill
              sizes="(max-width: 768px) 100vw, 50vw"
              className="object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#102542] via-[#102542]/40 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#ffb800] text-[#102542] text-xs font-bold uppercase tracking-wider mb-3">
                <MapPin className="w-3.5 h-3.5" />
                Terminal 1
              </div>
              <h3 className="text-2xl font-bold mb-1 drop-shadow-md">
                {t("standT1Title")}
              </h3>
              <p className="text-white/85 text-sm mb-4">{t("standT1Desc")}</p>
              <MapButton
                label={t("verEnMapa")}
                src="https://airportpanama.lmp.prod.livingmap.com/poi?feature=439d5cf545f1dd771a6cd423ad4a9e36&floor=1&lang=en-GB#18.35/9.067068/-79.387335/119"
              />
            </div>
          </div>

          <div className="group relative h-80 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-1">
            <Image
              src="https://galeria.tocumenaero.com/gallery/transporte/2282fed7-30ab-4189-8946-5208d1b1f946.jpg"
              alt="Stand de taxis Terminal 2"
              fill
              sizes="(max-width: 768px) 100vw, 50vw"
              className="object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#102542] via-[#102542]/40 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#ffb800] text-[#102542] text-xs font-bold uppercase tracking-wider mb-3">
                <MapPin className="w-3.5 h-3.5" />
                Terminal 2
              </div>
              <h3 className="text-2xl font-bold mb-1 drop-shadow-md">
                {t("standT2Title")}
              </h3>
              <p className="text-white/85 text-sm mb-4">{t("standT2Desc")}</p>
              <MapButton
                label={t("verEnMapa")}
                src="https://airportpanama.lmp.prod.livingmap.com/poi?feature=6d9683b8a39db181ab5cff99af52a2a0&floor=1&lang=en-GB#18.35/9.067736/-79.386954/119"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Formulario de cálculo y tarjetas de vehículos */}
      {RESERVAS_ACTIVAS && (
        <>
      <div
        id="reserva-taxi"
        className="relative w-screen mt-24 py-12 md:py-20 left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] overflow-hidden scroll-mt-24"
      >
        <div className="absolute inset-0">
          <Image
            src="https://galeria.tocumenaero.com/gallery/transporte/taxi1.JPG"
            alt=""
            fill
            sizes="(max-width: 768px) 100vw, 80vw"
            className="object-cover blur-sm scale-110"
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#102542]/75 via-[#102542]/10 to-[#102542]/20" />
        </div>

        <div className="relative w-[92%] md:max-w-2xl mx-auto bg-white p-6 md:p-8 rounded-2xl shadow-2xl border border-gray-200">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-[#102542]">
              {t("calc")}
            </h2>
            <div className="w-12 h-0.5 bg-[#102542] mx-auto mt-3 rounded-full" />
          </div>

          {/* Sección Trayecto */}
          <div className="mb-6">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-400 mb-4">
              {t("seccionTrayecto")}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Vuelo */}
              <div className="flex flex-col relative dropdown-vuelo sm:col-span-2">
                <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                  {t("labelVuelo")}
                </label>
                <button
                  type="button"
                  onClick={() => {
                    const nuevoEstado = !dropdownVueloAbierto;
                    setDropdownVueloAbierto(nuevoEstado);
                    if (nuevoEstado) cargarVuelos();
                  }}
                  className="bg-white border border-gray-200 hover:border-gray-400 focus:border-[#102542] rounded-lg px-3.5 py-3 text-sm flex items-center justify-between text-left transition-colors focus:outline-none"
                >
                  <span className="inline-flex items-center gap-2 min-w-0">
                    <Plane className="w-4 h-4 text-gray-400 shrink-0" />
                    <span
                      className={`truncate ${numeroVuelo ? "text-[#102542] font-medium" : "text-gray-400"}`}
                    >
                      {numeroVuelo || t("vuelo")}
                    </span>
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${
                      dropdownVueloAbierto ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {dropdownVueloAbierto && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-20 overflow-hidden">
                    <div className="p-2 border-b border-gray-100">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          value={busquedaVuelo}
                          onChange={(e) => setBusquedaVuelo(e.target.value)}
                          placeholder={t("vueloPh")}
                          className="w-full border border-gray-200 rounded-md py-2 pl-8 pr-2 text-sm focus:border-[#102542] focus:outline-none"
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={elegirTBC}
                      className="w-full text-left px-3.5 py-2.5 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none border-b border-gray-100 flex items-center justify-between"
                    >
                      <span className="font-semibold text-[#102542] text-sm">
                        TBC
                      </span>
                      <span className="text-xs text-gray-500">
                        {t("tbcDesc")}
                      </span>
                    </button>
                    <div className="max-h-56 overflow-y-auto">
                      {vuelosCargando && (
                        <div className="px-3 py-4 text-sm text-gray-500 text-center">
                          {t("cargandoVuelos")}
                        </div>
                      )}
                      {!vuelosCargando &&
                        vuelosCargados &&
                        vuelosFiltrados.length === 0 && (
                          <div className="px-3 py-4 text-sm text-gray-500 text-center">
                            {t("sinVuelos")}
                          </div>
                        )}
                      {!vuelosCargando &&
                        vuelosFiltrados.map((v) => {
                          const { codigo, hora, fecha: fechaVuelo } =
                            formatearVuelo(v);
                          return (
                            <button
                              key={`${v.id}-${codigo}`}
                              type="button"
                              onClick={() => elegirVuelo(v)}
                              className="w-full text-left px-3.5 py-2.5 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none border-b border-gray-100 last:border-b-0 flex items-center justify-between gap-2"
                            >
                              <div className="flex flex-col min-w-0">
                                <span className="font-medium text-[#102542] text-sm truncate">
                                  {codigo}
                                </span>
                                <span className="text-xs text-gray-500 truncate">
                                  {v.routingDescEs || v.routing}
                                </span>
                              </div>
                              {hora && (
                                <div className="shrink-0 text-right">
                                  <span className="block text-xs text-gray-500 font-mono">
                                    {hora}
                                  </span>
                                  <span className="block text-[10px] text-gray-400">
                                    {etiquetaDia(fechaVuelo)}
                                  </span>
                                </div>
                              )}
                            </button>
                          );
                        })}
                    </div>
                  </div>
                )}
              </div>

              {/* Destino (full width) */}
              <div className="flex flex-col relative dropdown-destino sm:col-span-2">
                <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                  {t("labelDestino")}
                </label>
                <button
                  type="button"
                  onClick={() =>
                    setDropdownDestinoAbierto(!dropdownDestinoAbierto)
                  }
                  className="bg-white border border-gray-200 hover:border-gray-400 focus:border-[#102542] rounded-lg px-3.5 py-3 text-sm flex items-center justify-between text-left transition-colors focus:outline-none"
                >
                  <span className="inline-flex items-center gap-2 min-w-0">
                    <MapPin className="w-4 h-4 text-gray-400 shrink-0" />
                    <span
                      className={`truncate ${destino ? "text-[#102542] font-medium" : "text-gray-400"}`}
                    >
                      {destino ? nombreDestino(destino) : t("destino")}
                    </span>
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${
                      dropdownDestinoAbierto ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {dropdownDestinoAbierto && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-20 overflow-hidden">
                    <div className="p-2 border-b border-gray-100">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          value={busquedaDestino}
                          onChange={(e) => setBusquedaDestino(e.target.value)}
                          placeholder={t("destinoPh")}
                          className="w-full border border-gray-200 rounded-md py-2 pl-8 pr-2 text-sm focus:border-[#102542] focus:outline-none"
                        />
                      </div>
                    </div>
                    <div className="max-h-56 overflow-y-auto">
                      {gruposDestino.map((grupo) => (
                        <div key={grupo.tipo}>
                          <p className="sticky top-0 bg-gray-50 px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-500">
                            {t(`tipoTarifa.${grupo.tipo}`)}
                          </p>
                          {grupo.tarifas.map((tarifa) => (
                            <button
                              key={tarifa.id}
                              type="button"
                              onClick={() => {
                                setDestino(tarifa);
                                setDropdownDestinoAbierto(false);
                                setBusquedaDestino("");
                              }}
                              className="w-full text-left px-3.5 py-2.5 text-sm hover:bg-gray-50 focus:bg-gray-50 focus:outline-none border-b border-gray-100 last:border-b-0 text-gray-700 flex items-center justify-between gap-2"
                            >
                              <span className="truncate">
                                {nombreDestino(tarifa)}
                                {tarifa.horas_incluidas ? (
                                  <span className="text-gray-400">
                                    {" "}
                                    ·{" "}
                                    {t("horasIncluidas", {
                                      n: tarifa.horas_incluidas,
                                    })}
                                  </span>
                                ) : null}
                              </span>
                              <span className="text-xs text-gray-500 shrink-0 font-mono">
                                ${formatearPrecio(Number(tarifa.precio_base))}
                              </span>
                            </button>
                          ))}
                        </div>
                      ))}
                      {tarifasCargando && (
                        <div className="px-3 py-4 text-sm text-gray-500 text-center">
                          {t("cargandoTarifas")}
                        </div>
                      )}
                      {!tarifasCargando &&
                        !hayResultadosDestino &&
                        (tarifasError ? (
                          <div className="px-3 py-4 text-center">
                            <p className="text-sm text-gray-500">
                              {t("errorTarifas")}
                            </p>
                            <button
                              type="button"
                              onClick={refrescarTarifas}
                              className="mt-2 text-sm font-semibold text-[#102542] underline underline-offset-4"
                            >
                              {t("reintentar")}
                            </button>
                          </div>
                        ) : (
                          <div className="px-3 py-4 text-sm text-gray-500 text-center">
                            {t("sinDestinos")}
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sección Reserva */}
          <div className="mb-6">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-400 mb-4">
              {t("seccionReserva")}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Con un vuelo elegido, la fecha y la hora salen de él; sólo se
                  piden a mano cuando el pasajero marca TBC o aún no eligió. */}
              {vueloSeleccionado ? (
                <div className="sm:col-span-2 flex flex-col">
                  <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                    {t("labelFechaHora")}
                  </label>
                  <div className="bg-[#102542]/5 border border-[#102542]/15 rounded-lg px-3.5 py-3 flex items-center justify-between gap-3">
                    <span className="inline-flex items-center gap-2 min-w-0">
                      <Plane className="w-4 h-4 text-[#102542] shrink-0" />
                      <span className="text-sm text-[#102542] font-medium truncate">
                        {formatearFecha(fecha)}
                        {hora ? ` · ${hora}` : ""}
                      </span>
                    </span>
                    <span className="text-xs text-gray-500 shrink-0">
                      {t("segunVuelo", { vuelo: numeroVuelo })}
                    </span>
                  </div>
                </div>
              ) : (
                <>
                {/* Fecha */}
                <div className="flex flex-col relative dropdown-fecha">
                  <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                    {t("labelFecha")}
                  </label>
                  <button
                    type="button"
                    onClick={() => setDropdownFechaAbierto(!dropdownFechaAbierto)}
                    className="bg-white border border-gray-200 hover:border-gray-400 focus:border-[#102542] rounded-lg px-3.5 py-3 text-sm flex items-center justify-between text-left transition-colors focus:outline-none"
                  >
                    <span className="inline-flex items-center gap-2 min-w-0">
                      <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
                      <span
                        className={`truncate ${fecha ? "text-[#102542] font-medium" : "text-gray-400"}`}
                      >
                        {fecha ? formatearFecha(fecha) : t("dia")}
                      </span>
                    </span>
                    <ChevronDown
                      className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${
                        dropdownFechaAbierto ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {dropdownFechaAbierto && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-10">
                      <div className="p-3">
                        <input
                          type="date"
                          value={fecha}
                          onChange={(e) => {
                            setFecha(e.target.value);
                            setDropdownFechaAbierto(false);
                          }}
                          min={getFechaMinima()}
                          className="w-full border border-gray-200 rounded-md p-2 text-sm focus:border-[#102542] focus:outline-none"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Hora */}
                <div className="flex flex-col relative dropdown-hora">
                  <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                    {t("labelHora")}
                  </label>
                  <button
                    type="button"
                    onClick={() => setDropdownHoraAbierto(!dropdownHoraAbierto)}
                    className="bg-white border border-gray-200 hover:border-gray-400 focus:border-[#102542] rounded-lg px-3.5 py-3 text-sm flex items-center justify-between text-left transition-colors focus:outline-none"
                  >
                    <span className="inline-flex items-center gap-2 min-w-0">
                      <Clock className="w-4 h-4 text-gray-400 shrink-0" />
                      <span
                        className={`truncate ${hora ? "text-[#102542] font-medium" : "text-gray-400"}`}
                      >
                        {hora || t("hora")}
                      </span>
                    </span>
                    <ChevronDown
                      className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${
                        dropdownHoraAbierto ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {dropdownHoraAbierto && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-10 max-h-48 overflow-y-auto">
                      {horas.map((horaOption, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => {
                            setHora(horaOption);
                            setDropdownHoraAbierto(false);
                          }}
                          className="w-full text-left px-3.5 py-2.5 text-sm hover:bg-gray-50 focus:bg-gray-50 focus:outline-none border-b border-gray-100 last:border-b-0 text-gray-700 font-mono"
                        >
                          {horaOption}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                </>
              )}

              {/* Pasajeros (full width with inline stepper) */}
              <div className="flex flex-col sm:col-span-2">
                <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                  {t("labelPasajeros")}
                </label>
                <div className="bg-white border border-gray-200 rounded-lg px-3.5 py-2.5 flex items-center justify-between">
                  <span className="inline-flex items-center gap-2 text-sm text-[#102542] font-medium">
                    <Users className="w-4 h-4 text-gray-400" />
                    {`${pasajeros} ${t("pasajero")}${pasajeros > 1 ? "s" : ""}`}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setPasajeros(Math.max(1, pasajeros - 1))}
                      disabled={pasajeros <= 1}
                      className="w-8 h-8 rounded-full border border-gray-200 hover:border-[#102542] hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center text-[#102542] font-bold transition-colors"
                      aria-label="-1"
                    >
                      −
                    </button>
                    <span className="w-6 text-center font-semibold text-[#102542] text-sm">
                      {pasajeros}
                    </span>
                    <button
                      type="button"
                      onClick={() => setPasajeros(Math.min(4, pasajeros + 1))}
                      disabled={pasajeros >= 4}
                      className="w-8 h-8 rounded-full border border-gray-200 hover:border-[#102542] hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center text-[#102542] font-bold transition-colors"
                      aria-label="+1"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Acciones */}
          <div className="flex items-center justify-between gap-3 pt-5 border-t border-gray-100">
            <button
              type="button"
              onClick={() => {
                setDestino(null);
                setFecha("");
                setHora("");
                setPasajeros(1);
                setNumeroVuelo("");
                setVueloSeleccionado(null);
                setBusquedaVuelo("");
                setBusquedaDestino("");
                setMostrarResultado(false);
                setDropdownPasajerosAbierto(false);
                setDropdownFechaAbierto(false);
                setDropdownDestinoAbierto(false);
                setDropdownHoraAbierto(false);
                setDropdownVueloAbierto(false);
              }}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-[#102542] transition-colors"
            >
              <X className="w-3.5 h-3.5" />
              {t("limpiar")}
            </button>
            <button
              type="button"
              onClick={() => {
                if (destino && fecha && hora) {
                  setMostrarResultado(true);
                  showToast(
                    "success",
                    "¡Listo!",
                    "Te mostramos las opciones de vehículos disponibles.",
                  );
                  requestAnimationFrame(() => {
                    document
                      .getElementById("resultados-vehiculos")
                      ?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                  });
                } else {
                  const faltantes: string[] = [];
                  if (!destino) faltantes.push("destino");
                  if (!fecha) faltantes.push("fecha");
                  if (!hora) faltantes.push("hora");
                  showToast(
                    "warning",
                    "Faltan datos para buscar",
                    `Por favor selecciona ${faltantes.join(", ")}.`,
                  );
                }
              }}
              className="inline-flex items-center gap-2 bg-[#102542] hover:bg-[#1B3B5F] text-white px-6 py-2.5 rounded-lg font-semibold text-sm shadow-sm hover:shadow-md transition-all"
            >
              <Search className="w-4 h-4" />
              {t("buscar")}
            </button>
          </div>
        </div>
      </div>
      {/* Resultado */}
      {mostrarResultado &&
        (() => {
          // Se nombran por categoría, no por modelo: la cooperativa asigna el
          // carro que tenga disponible dentro del tipo que se reservó.
          const vehiculos: {
            tipo: TipoVehiculo;
            img: string;
            offsetClass: string;
            contentMargin: string;
          }[] = [
            {
              tipo: "suv",
              img: "/tucson.png",
              offsetClass: "-top-24",
              contentMargin: "-mt-24",
            },
            {
              tipo: "sedan",
              img: "/cerato.png",
              offsetClass: "-top-15",
              contentMargin: "-mt-12",
            },
            {
              tipo: "van",
              img: "/hiace.png",
              offsetClass: "-top-15",
              contentMargin: "-mt-10",
            },
          ];
          return (
            <div
              id="resultados-vehiculos"
              className="w-[92%] md:w-full md:max-w-5xl mx-auto mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 scroll-mt-24"
            >
              {vehiculos.map((v) => (
                <div
                  key={v.tipo}
                  className="bg-white rounded-2xl shadow-sm hover:shadow-lg border border-gray-200 hover:border-[#102542]/40 mt-28 pb-6 px-5 text-center transition-all duration-300"
                >
                  <Image
                    src={v.img}
                    alt={t(`vehiculo.${v.tipo}`)}
                    width={500}
                    height={300}
                    className={`relative ${v.offsetClass} mx-auto`}
                  />
                  <div className={v.contentMargin}>
                    <p className="font-semibold text-[#102542] text-base">
                      {t(`vehiculo.${v.tipo}`)}
                    </p>
                    <p className="text-sm text-gray-500 mb-5">
                      <span className="font-bold text-[#102542]">2</span>{" "}
                      {t("maletas")}
                    </p>
                    <div className="flex justify-center items-baseline gap-0.5 mb-2">
                      <span className="text-sm font-bold text-[#102542] self-start mt-1">
                        $
                      </span>
                      <span className="text-3xl font-black text-[#102542] leading-none">
                        {precioActual ? formatearPrecio(precioActual) : "—"}
                      </span>
                    </div>
                    <p className="text-[11px] text-gray-500 mb-5">
                      {t("imagenReferencia")}
                    </p>
                    <button
                      type="button"
                      onClick={() => abrirModalReserva(v.tipo)}
                      className="w-full inline-flex items-center justify-center gap-2 bg-[#102542] hover:bg-[#1B3B5F] text-white px-6 py-2.5 rounded-lg font-semibold text-sm shadow-sm hover:shadow-md transition-all"
                    >
                      {t("reserva")}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
        </>
      )}

      {/* Alternativa mientras la reserva está apagada. */}
      {!RESERVAS_ACTIVAS && <TarifasTaxi />}

      <section className="w-full mt-24 bg-[#102542] rounded-3xl overflow-hidden relative">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-[#ffb800] rounded-full blur-3xl" />
        </div>
        <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-0">
          <div className="p-8 md:p-12 flex flex-col justify-center">
            <span className="text-[#ffb800] text-sm uppercase tracking-widest font-semibold">
              {t("contactoLabel")}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-3 mb-4">
              {t("ayudaTitle")}
            </h2>
            <p className="text-white/80 leading-relaxed">{t("ayudaDesc")}</p>
          </div>
          <div className="p-4 lg:p-8 flex flex-col gap-3 justify-center">
            <a
              href="https://wa.me/50767112645"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#ffb800]/40 rounded-2xl p-4 transition-all"
            >
              <div className="w-12 h-12 rounded-xl bg-[#ffb800] text-[#102542] flex items-center justify-center shrink-0">
                <FaWhatsapp className="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs uppercase tracking-wider text-[#ffb800] font-semibold">
                  {t("contactWhatsapp")}
                </p>
                <p className="text-white font-semibold">(+507) 6711-2645</p>
              </div>
            </a>
            <a
              href="tel:+5078311837"
              className="group flex items-center gap-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#ffb800]/40 rounded-2xl p-4 transition-all"
            >
              <div className="w-12 h-12 rounded-xl bg-[#ffb800] text-[#102542] flex items-center justify-center shrink-0">
                <VscCallOutgoing className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs uppercase tracking-wider text-[#ffb800] font-semibold">
                  {t("contactLlamar")}
                </p>
                <p className="text-white font-semibold">(+507) 831-1837</p>
              </div>
            </a>
            <a
              href="mailto:admoncoop.exptoc01@gmail.com"
              className="group flex items-center gap-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#ffb800]/40 rounded-2xl p-4 transition-all"
            >
              <div className="w-12 h-12 rounded-xl bg-[#ffb800] text-[#102542] flex items-center justify-center shrink-0">
                <CiMail className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs uppercase tracking-wider text-[#ffb800] font-semibold">
                  {t("contactCorreo")}
                </p>
                <p className="text-white font-semibold truncate">
                  admoncoop.exptoc01@gmail.com
                </p>
              </div>
            </a>
          </div>
        </div>
      </section>

      <OtrasOpciones actual="taxi" />

      {datosReservaTemp && resumenReserva && (
        <TaxiReservaModal
          isOpen={modalAbierto}
          onClose={() => setModalAbierto(false)}
          reservaData={datosReservaTemp}
          resumen={resumenReserva}
          tipoVehiculo={tipoVehiculoSeleccionado}
        />
      )}

      <ToastContainer toasts={toasts} onClose={closeToast} />
    </LayoutTransporte>
  );
}
